import * as React from 'react';
import ReactDatePicker from 'react-datepicker';

import { dateParser } from '@common/dateParser';

import { CILNotificationViewModel } from '@services/CILNotificationService';

export enum CILNotificationCILComponentPage {
  CIL,
  CILAcceptance
}

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface CILNotificationCILComponentProperties {
  readonly date?: Date;
  readonly isDateInvalid: boolean;
  readonly isSubmitEnabled: boolean;
  readonly page: CILNotificationCILComponentPage;
  readonly viewModel: CILNotificationViewModel;

  readonly checkBox: () => void;
  readonly checkDate: (date: Date) => void;
  readonly checkSignature: (signature: string) => void;
  readonly gotoPage: (page: CILNotificationCILComponentPage) => void;
  readonly leave: () => void;
  readonly submitCIL: () => void;
}

// Component definition
export class CILNotificationCILComponent extends React.PureComponent<CILNotificationCILComponentProperties> {
  private static formatMoney(input: number): string {
    if (input === 0) {
      return '$0.00';
    }
    return input.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
  }

  public render(): JSX.Element {
    return this.props.page === CILNotificationCILComponentPage.CIL ? this.renderCIL() : this.renderCILAcceptance();
  }

  private renderCIL(): JSX.Element {
    let cilAmount: number = this.props.viewModel.Cash_In_Lieu_Am ? parseFloat(this.props.viewModel.Cash_In_Lieu_Am) : 0;

    const titleText =
      this.props.viewModel.CIL_Only_Fl && this.props.viewModel.CIL_Only_Fl === true ? 'Cash-in-Lieu' : 'Option #2';

    return (
      <div className="text-left">
        <h2>{titleText}</h2>
        <br />
        <p>
          Fidelity National Home Warranty has reviewed the service work order for your{' '}
          {this.props.viewModel.Appliance_Type} and are offering cash-in-lieu of replacement in the amount of $
          {CILNotificationCILComponent.formatMoney(cilAmount)}.
        </p>
        <p>
          *Comprehensive Contracts: Total cash-in-lieu amount includes FNHW's cost of replacement unit, tax, delivery,
          basic installation, and disposal costs.
        </p>
        <p>
          *Standard Contracts: Total cash-in-lieu amount includes FNHW's cost of replacement unit, tax, delivery, basic
          installation only (disposal not covered).
        </p>
        <p>
          <b>
            <u>Benefits of accepting "Cash-in-Lieu" over "Replacement":</u>
          </b>
        </p>
        <ul>
          <li>
            <b>
              <u>Allows you to:</u>
            </b>
          </li>
          <ul>
            <li>Purchase any appliance of your choice</li>
            <li>Take advantage of sales/special deals that may not be available to FNHW.</li>
            <li>
              Control delivery of the appliance on{' '}
              <b>
                <i>your</i>
              </b>{' '}
              schedule.
            </li>
          </ul>
        </ul>
        <p>If you select this option:</p>
        <ul>
          <li>
            Fidelity National Home Warranty will mail you a check in the amount above (mailed within 7-10 business dates
            upon acceptance)
          </li>
          <li>
            Once you have purchased a new unit, you will need to provide proof-of-purchase to FNHW for future service on
            this unit by logging into your account and selecting Submissions from the top menu.
          </li>
        </ul>
        <div className="text-center">
          <button
            className="btn btn-primary"
            onClick={() => this.props.gotoPage(CILNotificationCILComponentPage.CILAcceptance)}
          >
            Select Cash-in-Lieu
          </button>
          {this.renderViewReplacementButton()}
        </div>
      </div>
    );
  }

  private renderCILAcceptance(): JSX.Element {
    if (!this.props.viewModel) {
      return (
        <div className="alert alert-danger" role="alert">
          <strong>Not Found!</strong>
        </div>
      );
    }

    let cilAmount: number = this.props.viewModel.Cash_In_Lieu_Am ? parseFloat(this.props.viewModel.Cash_In_Lieu_Am) : 0;

    let datePickerClasses = this.props.isDateInvalid ? { className: 'is-invalid' } : { className: '' };

    const titleText =
      this.props.viewModel.CIL_Only_Fl && this.props.viewModel.CIL_Only_Fl === true ? 'Cash-in-Lieu' : 'Option #2';

    return (
      <div className="text-left">
        <h2>{titleText}</h2>
        <br />

        <div className="row middle-xs">
          <div className="col-sm-1 col-xs-2 text-center">
            <input onClick={this.handleCheckBox} type="checkbox" />
          </div>
          <div className="col-sm-11 col-xs-10">
            <p>
              I understand the total amount offered to me includes FNHW’s actual cost to replace the appliance including
              delivery, installation and disposal (when applicable).
            </p>
          </div>
        </div>

        <div className="row middle-xs">
          <div className="col-sm-1 col-xs-2 text-center">
            <input onClick={this.handleCheckBox} type="checkbox" />
          </div>
          <div className="col-sm-11 col-xs-10">
            <p>
              I understand that upon acceptance of cash-in-lieu, I am responsible for the purchase, delivery and
              installation of a new unit. The amount provided as cash-in-lieu is generally less than retail.
            </p>
          </div>
        </div>

        <div className="row middle-xs">
          <div className="col-sm-1 col-xs-2 text-center">
            <input onClick={this.handleCheckBox} type="checkbox" />
          </div>
          <div className="col-sm-11 col-xs-10">
            <p>I understand that any unpaid service fees will be deducted from my total cash-in-lieu amount.</p>
          </div>
        </div>

        <hr />

        <p>
          I am accepting cash-in-lieu of replacement. A check for ${CILNotificationCILComponent.formatMoney(cilAmount)}
          will be mailed in 7-10 business days and FNHW considers this claim resolved.
        </p>

        <div className="form-control">
          <label>Contract Holder</label>
          <input onChange={event => this.props.checkSignature(event.currentTarget.value)} type="text" />
        </div>
        <div className="form-control">
          <label>Date</label>
          <ReactDatePicker
            {...datePickerClasses}
            onChange={m => m && this.props.checkDate(m.toDate())}
            selected={this.props.date && dateParser.parse(this.props.date)}
            value={this.props.date && dateParser.parse(this.props.date).format('MM/DD/YYYY')}
          />
          {this.props.isDateInvalid && (
            <div className="error">
              <p>Please enter today's date</p>
            </div>
          )}
        </div>
        <br />
        <div className="text-center">
          <button className="btn btn-primary" disabled={!this.props.isSubmitEnabled} onClick={this.props.submitCIL}>
            I AGREE / SUBMIT
          </button>
          {this.renderViewReplacementButton()}
        </div>
      </div>
    );
  }

  private handleCheckBox = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.disabled = true;
    this.props.checkBox();
  };

  private renderViewReplacementButton(): JSX.Element {
    if (this.props.viewModel.CIL_Only_Fl !== true) {
      return (
        <button className="btn btn-secondary" onClick={this.props.leave}>
          View Option #1: Replacement
        </button>
      );
    }
    return <></>;
  }
}



// WEBPACK FOOTER //
// ./src/cil-notification/_components/cil/_components/cil.component.tsx